import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomTable from "../../../components/custom-table/custom-table";
import AppealInfoModal from "../../../components/modals/appealInfoModal/appealInfoModal";
import OncologyFileUploadModal from "../../../components/modals/OncologyFileUpload/oncologyFileUploadModal";
import SendToAppealModal from "../../../components/modals/sendToAppealModal/sendToAppealModal";
import SftpDocModal from "../../../components/modals/sftpDocModal/sftpDocModal";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { CaseAppealsTableRowData } from "../../../data-model/caseAppealsTable";
import { FilterSection, FilterValues } from "../../../data-model/filterValues";
import { OncologySubmissionTableDataModel } from "../../../data-model/tablesData";
import { ModalId } from "../../../enums/modalId";
import { TableNames } from "../../../enums/tableNames";
import { useModalOpener } from "../../../hooks/useModalOpener";
import { setFilter } from "../../../redux/slice/filterValuesSlice";
import {
  closeModal,
  setOpenedModalId,
} from "../../../redux/slice/modalSliceNew";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { handleOpenSubmissionSummary } from "../../pharmaStpFile/actionHandlers/openSubmissionSummaryHandler";
import { shouldOpenCmmModal } from "../../pharmaStpFile/table/utils/shouldOpenCmmModal";
import { handleOncologyFileUploadClick } from "../actionHandlers/oncologyFileUploadHandler";
import {
  canOpenAppealDetailsModal,
  canOpenSendToAppealModal,
} from "../actionHandlers/onSendToAppealClick";
import { useFilterAndSearch } from "../hooks/useFilterAndSearch";
import { useInitialDataFetchingForTable } from "../hooks/useInitialDataFetchingForTable";
import { useNextPageDataFetch } from "../hooks/useNextPageDataFetch";
import { useOncologySubmissionTableData } from "../hooks/useOncologySubmissionTableData";
import {
  generateTableDataForOncologySubmission,
  OncologySubmissionTableHeader,
} from "../table/oncologySubmissionTableData";
import { getBiosimilarPopupItemsForDrug } from "../utils/biosimilarDrugOptions";
import { createAllFilterSections } from "../utils/createFilterSections";
import { mapRowToAppealInfoModalData } from "../utils/mapRowToAppealInfoModalData";
import {
  areAllRowsOnPageSelected,
  deselectRowsOnCurrentPage,
  selectRowsOnCurrentPage,
  toggleCheckboxSelection,
} from "../utils/toggleCheckboxSelection";
import BiosimilarRetryPopup from "./biosimilarRetryPopup";

/**
 * Oncology submission orders table with File Upload and Run Delivery Pipeline actions.
 */
const OncologySubmissionOrdersTable = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { openedModalId } = useSelector(
    (state: RootState) => state.modalSliceNew,
  );
  const { getTableDataFromContext, setTableDataForContext } =
    useTablesContext();

  useModalOpener();

  const getDateRange = () => {
    // Date format: YYYYMMDD (e.g., "20251201")
    const today = new Date();
    const twoMonthsAgo = new Date(today);
    twoMonthsAgo.setMonth(today.getMonth() - 2);

    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}${month}${day}`;
    };

    const startDate = formatDate(twoMonthsAgo);
    const endDate = formatDate(today);

    return { startDate, endDate };
  };

  const { startDate, endDate } = getDateRange();
  const pageSize = 120;

  const [filterData, setFilterData] = useState<FilterSection[]>([]);

  const {
    oncologySubmissionState,
    setOncologySubmissionState,
    tableData,
    setTableData,
  } = useOncologySubmissionTableData();

  const { initialDataFetchingExecuted, setInitialDataFetchingExecuted } =
    useInitialDataFetchingForTable(
      setOncologySubmissionState,
      setTableData,
      startDate,
      endDate,
      pageSize,
    );

  const {
    paginationResetCount,
    setSearchTerm,
    searchTerm,
    validFilters,
    refetchWithCurrentSearchAndFilters,
  } = useFilterAndSearch(
    initialDataFetchingExecuted,
    setTableData,
    setOncologySubmissionState,
    startDate,
    endDate,
    pageSize,
  );

  const { setEndIndex } = useNextPageDataFetch(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    oncologySubmissionState,
    setOncologySubmissionState,
    setTableData,
    startDate,
    endDate,
    pageSize,
  );

  const [allOncologyOrdersTableData, setAllOncologyOrdersTableData] = useState<
    OncologySubmissionTableDataModel[]
  >([]);

  const [biosimilarRetryRow, setBiosimilarRetryRow] =
    useState<OncologySubmissionTableDataModel | null>(null);

  const [sendToAppealRow, setSendToAppealRow] =
    useState<OncologySubmissionTableDataModel | null>(null);

  const [appealInfoRowData, setAppealInfoRowData] =
    useState<CaseAppealsTableRowData | null>(null);

  const [, setAreAllRowsSelected] = useState<boolean>(false);

  // Track current page indices for select all functionality
  const [currentPageStartIndex, setCurrentPageStartIndex] = useState<number>(0);
  const [currentPageEndIndex, setCurrentPageEndIndex] = useState<number>(9); // Default to itemsPerPage - 1

  useEffect(() => {
    const formattedTableData =
      generateTableDataForOncologySubmission(tableData);
    setAllOncologyOrdersTableData(formattedTableData);

    setAreAllRowsSelected(false);
  }, [tableData]);

  const handleToggleCheckboxSelection = useCallback((id: string) => {
    toggleCheckboxSelection(
      id,
      setAllOncologyOrdersTableData,
      () => {}, // no-op for setHasSelectedRows
      setAreAllRowsSelected,
    );
  }, []);

  // Handle select all / deselect all rows on current page only
  const handleSelectAll = useCallback(() => {
    const allOnPageSelected = areAllRowsOnPageSelected(
      allOncologyOrdersTableData,
      currentPageStartIndex,
      currentPageEndIndex,
    );

    if (allOnPageSelected) {
      // If all on current page are selected, deselect them
      deselectRowsOnCurrentPage(
        setAllOncologyOrdersTableData,
        currentPageStartIndex,
        currentPageEndIndex,
        setAreAllRowsSelected,
      );
    } else {
      // If not all on current page selected, select them
      selectRowsOnCurrentPage(
        setAllOncologyOrdersTableData,
        currentPageStartIndex,
        currentPageEndIndex,
        setAreAllRowsSelected,
      );
    }
  }, [allOncologyOrdersTableData, currentPageStartIndex, currentPageEndIndex]);

  const handleSearch = (searchText: string) => {
    setSearchTerm(searchText);
  };

  /**
   * Biosimilar column badge-button: enabled "View" refetches by sequence; enabled "Retry" opens `PopupWithApi`.
   *
   * @param rowData - Formatted table row including `biosimilarCase` badge state and `sequence` / `seq`
   */
  const handleBiosimilarCaseBadgeClick = useCallback(
    (rowData: OncologySubmissionTableDataModel) => {
      const badge = rowData?.biosimilarCase;
      if (badge?.isDisabled) {
        return;
      }

      if (badge?.text === "View") {
        const seqForSearch = String(
          rowData.sequence ?? rowData.seq ?? "",
        ).trim();
        if (!seqForSearch) {
          return;
        }
        refetchWithCurrentSearchAndFilters(seqForSearch);
        return;
      }

      if (badge?.text === "Retry") {
        const popupItems = getBiosimilarPopupItemsForDrug(rowData.drug);
        if (popupItems.length === 0) {
          toast.error(
            `No biosimilar options found for "${rowData.drug ?? "this drug"}".`,
          );
          return;
        }
        setBiosimilarRetryRow(rowData);
      }
    },
    [refetchWithCurrentSearchAndFilters],
  );

  const handleCloseBiosimilarRetryPopup = useCallback(() => {
    setBiosimilarRetryRow(null);
  }, []);

  const handleBiosimilarRetrySuccess = useCallback(() => {
    setInitialDataFetchingExecuted(false);
  }, [setInitialDataFetchingExecuted]);

  /**
   * Send to Appeal column: View opens the same Appeal Info modal as Case Appeals
   * Edit; Send opens generate/send modal.
   *
   * @param rowData - Formatted table row including `sendToAppeal` badge state
   */
  const handleSendToAppealBadgeClick = useCallback(
    (rowData: OncologySubmissionTableDataModel) => {
      if (canOpenAppealDetailsModal(rowData)) {
        setAppealInfoRowData(mapRowToAppealInfoModalData(rowData));
        return;
      }

      if (canOpenSendToAppealModal(rowData)) {
        setSendToAppealRow(rowData);
      }
    },
    [],
  );

  /**
   * Closes the Appeal Info modal opened from Send to Appeal View.
   */
  const handleCloseAppealInfoModal = useCallback(() => {
    setAppealInfoRowData(null);
  }, []);

  /**
   * Refetches the table after a successful Appeal Info save from View.
   */
  const handleAppealInfoSuccess = useCallback(() => {
    setInitialDataFetchingExecuted(false);
  }, [setInitialDataFetchingExecuted]);

  const handleCloseSendToAppealModal = useCallback(() => {
    setSendToAppealRow(null);
  }, []);

  const handleSendToAppealSuccess = useCallback(() => {
    setInitialDataFetchingExecuted(false);
  }, [setInitialDataFetchingExecuted]);

  const handleRefresh = () => {
    setInitialDataFetchingExecuted(false);
  };

  const handleViewCmmClick = (rowData: any) => {
    if (shouldOpenCmmModal(rowData.cmmStatus)) {
      dispatch(
        setOpenedModalId({
          id: ModalId.SFTP_DOC_MODAL,
          metaData: {
            selectedRowData: rowData,
          },
        }),
      );
    }
  };

  const handleRowClick = (rowData: any) => {};

  const handleCloseSftpDocModal = () => {
    dispatch(closeModal());
  };

  /**
   * Opens the same Submission Summary modal used by the STP file table Add button.
   *
   * @input rowData - Formatted table row; raw API row is preferred when available
   */
  const handleAddKeyButtonClick = (rowData: any) => {
    const rawData =
      tableData?.find((r: any) => r.identifier === rowData.identifier) ??
      rowData;
    handleOpenSubmissionSummary(dispatch, rawData);
  };

  useEffect(() => {
    if (tableData && tableData.length > 0) {
      const filters = createAllFilterSections(tableData);
      setFilterData(filters);
    }
  }, [tableData]);

  const handleFilterApply = (data: FilterValues) => {
    dispatch(setFilter(data));
  };

  // Watch for shouldRefetch flag from context
  const tableContextData = getTableDataFromContext(
    TableNames.ONCOLOGY_SUBMISSION_ORDERS,
  );

  useEffect(() => {
    if (tableContextData?.shouldRefetch) {
      // Trigger refetch
      setInitialDataFetchingExecuted(false);
      // Reset the flag
      setTableDataForContext(TableNames.ONCOLOGY_SUBMISSION_ORDERS, {
        shouldRefetch: false,
      });
    }
  }, [
    tableContextData?.shouldRefetch,
    setInitialDataFetchingExecuted,
    setTableDataForContext,
  ]);

  if (oncologySubmissionState.error) {
    console.error(
      "Error fetching oncology submission data:",
      oncologySubmissionState.error,
    );
  }

  const handleOncologyFileUpload = useCallback(() => {
    handleOncologyFileUploadClick(dispatch);
  }, [dispatch]);

  /**
   * Opens the Run Delivery Pipeline modal (same flow as Pharma STP File).
   */
  const handleRunDeliveryPipelineClick = useCallback(() => {
    dispatch(setOpenedModalId({ id: ModalId.RUN_DELIVERY_PIPELINE_MODAL }));
  }, [dispatch]);

  const handleOncologyFileUploadSuccess = useCallback(() => {
    setInitialDataFetchingExecuted(false);
  }, [setInitialDataFetchingExecuted]);

  return (
    <>
      <CustomTable
        tableHeaders={OncologySubmissionTableHeader}
        tableData={allOncologyOrdersTableData}
        itemsPerPage={10}
        pagesPerView={4}
        count={allOncologyOrdersTableData.length}
        totalCount={oncologySubmissionState.data?.total_count ?? 0}
        isFetching={oncologySubmissionState.loading}
        tableName={TableNames.ONCOLOGY_SUBMISSION_ORDERS}
        searchingText={handleSearch}
        placeholder="Search by RxNumber and Patient Name"
        onViewCmmClick={(data) => handleViewCmmClick(data)}
        onBiosimilarCaseBadgeClick={handleBiosimilarCaseBadgeClick}
        onSendToAppealBadgeClick={handleSendToAppealBadgeClick}
        // onTextNoteClick={(data) => handleTextNoteClick(data)}
        onRefreshButton={handleRefresh}
        onRowClick={(data) => handleRowClick(data)}
        isDisabled={oncologySubmissionState.loading}
        showRefreshButton={true}
        showOtherButton={true}
        otherButtonMeta={[
          {
            text: "Run Delivery Pipeline",
            disabled: oncologySubmissionState.loading,
            buttonType: "secondary",
            size: "medium",
            onClick: handleRunDeliveryPipelineClick,
          },
          {
            text: "File Upload",
            disabled: oncologySubmissionState.loading,
            buttonType: "primary",
            size: "medium",
            onClick: handleOncologyFileUpload,
          },
        ]}
        showFilterButton={true}
        filterSectionsData={filterData}
        onFilterApplyClicked={handleFilterApply}
        endIndexOfTable={(endIndex) => {
          setEndIndex(endIndex);
          setCurrentPageEndIndex(endIndex);
        }}
        startIndexOfTable={setCurrentPageStartIndex}
        requestForPaginationReset={paginationResetCount}
        showInLineLoader={oncologySubmissionState.showInlineLoader}
        onAddKeyButtonClick={handleAddKeyButtonClick}
        showButtonWithDropdown={false}
        toggleCheckboxSelection={handleToggleCheckboxSelection}
        isCheckboxSelected={areAllRowsOnPageSelected(
          allOncologyOrdersTableData,
          currentPageStartIndex,
          currentPageEndIndex,
        )}
        onSelectAll={handleSelectAll}
      />
      {openedModalId === ModalId.SFTP_DOC_MODAL && (
        <SftpDocModal onClose={handleCloseSftpDocModal} />
      )}
      {biosimilarRetryRow && (
        <BiosimilarRetryPopup
          rowData={biosimilarRetryRow}
          onClose={handleCloseBiosimilarRetryPopup}
          onSuccess={handleBiosimilarRetrySuccess}
        />
      )}
      {sendToAppealRow && (
        <SendToAppealModal
          rowData={sendToAppealRow}
          onClose={handleCloseSendToAppealModal}
          onSuccess={handleSendToAppealSuccess}
        />
      )}
      {appealInfoRowData && (
        <AppealInfoModal
          key={appealInfoRowData.identifier}
          data={appealInfoRowData}
          onClose={handleCloseAppealInfoModal}
          onSuccess={handleAppealInfoSuccess}
        />
      )}
      {openedModalId === ModalId.ONCOLOGY_FILE_UPLOAD_MODAL && (
        <OncologyFileUploadModal
          onClose={() => dispatch(closeModal())}
          onSuccess={handleOncologyFileUploadSuccess}
        />
      )}
    </>
  );
};

export default OncologySubmissionOrdersTable;
